import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnInit,
  Optional,
  QueryList,
  Self,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors } from '@angular/forms';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { RadioItem } from '@shared/models/step.model';
import { I18nService } from '@app/i18n';
import { ButtonPadding, ButtonVariant } from '@app/components/button/button.component';
import { MatExpansionPanel } from '@angular/material/expansion';

let identifier: number = 0;

@Component({
  selector: 'pl-radio',
  templateUrl: './pl-radio.component.html',
  styleUrls: ['./pl-radio.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlRadioComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) public label: string = '';
  @Input() public subLabel: string | null = null;
  @Input() public isDisabled: boolean = false;
  @Input() public isFormSubmitted: boolean = false;
  @Input() public isRequired: boolean = false;
  @Input() public minItemsDisplayed: number = 0;

  public radioValue: string | null = null;

  public items: RadioItem[] = [];

  public selectMessage: string = `(${this.i18nService.translate(`blocks.checkbox.please_select_x_options`, {
    count: 1,
  })})`;

  public identifier: string = `form-pl-radio-${identifier++}`;

  private onChangeCallback!: (value: RadioItem[]) => void;
  private onTouchedCallback!: () => void;

  public readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  public readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  public readonly PlIcon: typeof PlIcon = PlIcon;
  public readonly IconFrame: typeof IconFrame = IconFrame;

  public isPanelOpen: boolean = false;

  @HostBinding('tabindex') public tabindex: number = 1;
  @HostListener('focus') public onFocus(): void {
    console.log('\x1b[41m TONIO PlRadioComponent onFocus label=', this.label, '\x1b');
    if (this.radioItems && this.radioItems.length > 0) {
      // preventScroll: true because scroll is handled by directive plScrollToInvalidControlOnSubmit
      this.radioItems.first.nativeElement.focus({ preventScroll: true });
    }
  }

  @ViewChild(MatExpansionPanel) public matExpansionPanel!: MatExpansionPanel;
  @ViewChildren('radioItems', { read: ElementRef }) public radioItems!: QueryList<ElementRef>;

  constructor(
    @Optional() @Self() public controlDir: NgControl,
    private i18nService: I18nService,
  ) {
    if (this.controlDir) {
      this.controlDir.valueAccessor = this;
    }
  }

  public ngOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        control.setValidators([this.validate.bind(this)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    if (this.subLabel) {
      this.selectMessage = this.subLabel;
    }
  }

  private validate(): ValidationErrors | null {
    const allErrors: any = {};

    if (this.isRequired && this.items.length > 0 && this.radioValue === null) {
      allErrors.required = true;
    }

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public registerOnChange(fn: (value: RadioItem[]) => void): void {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public writeValue(itemsToWrite: RadioItem[] | null): void {
    if (itemsToWrite !== undefined && itemsToWrite !== null) {
      this.items = [...itemsToWrite];
      this.radioValue = itemsToWrite.find((item: RadioItem) => item.selected)?.value || null;
      this.displayAllRadioIfItemSelectedIsNotVisible(itemsToWrite);
    }
    this.updateValueAndValidity();
  }

  private displayAllRadioIfItemSelectedIsNotVisible(items: RadioItem[]): void {
    for (const [index, item] of items.entries()) {
      if (item.selected && index >= this.minItemsDisplayed) {
        this.isPanelOpen = true;
        return;
      }
    }
  }

  public onTouched(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  public onRadioItemToggle(radioValue: string | null): void {
    this.items = this.items.map((item: RadioItem): RadioItem => {
      return { ...item, selected: item.value === radioValue };
    });
    this.emitChanges();
  }

  private emitChanges(): void {
    this.onChangeCallback(this.items);
    this.onTouched();
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }

  public toggleExpansionPanel(): void {
    this.matExpansionPanel.toggle();
  }
}
